import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress } from '@mui/material';
import { useSalonsListQuery, useTeamsTeamsCreateMutation, useAuthProfileRetrieveQuery } from '../../../../services/GLOWClient';
import { teamsApi } from '../../../../services/teamsApi';
import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import GroupsIcon from '@mui/icons-material/Groups';

const AddTeam = ({ open, onClose, onSuccess, selectedSalonId = null }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        salon: selectedSalonId || ''
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    
    // Get current user profile to filter salons by owner
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    
    // Fetch salons - only when no selectedSalonId is provided
    const { data: salonsData, isLoading: salonsLoading } = useSalonsListQuery(
        userProfile && !selectedSalonId ? { owner: userProfile.id } : { skip: true }
    );
    
    const [createTeam, { isLoading: isCreating }] = useTeamsTeamsCreateMutation();
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = t('pro_team_member.add_team.team_name_required');
        if (!formData.salon && !selectedSalonId) newErrors.salon = t('pro_team_member.add_team.select_salon_required');
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        try {
            // Format the data according to the API requirements
            const createData = {
                teamRequest: {
                    name: formData.name.trim(),
                    salon: Number(formData.salon),
                    description: formData.description?.trim() || ''
                }
            };
            
            
            const result = await createTeam(createData).unwrap();

            // Invalidate cached salon teams so dialogs like "Créer un membre" show the new team without refresh
            const salonIdNum = Number(formData.salon);
            if (!Number.isNaN(salonIdNum)) {
                dispatch(teamsApi.util.invalidateTags([{ type: 'Teams', id: `salon-${salonIdNum}` }]));
            } else {
                dispatch(teamsApi.util.invalidateTags([{ type: 'Teams', id: 'LIST' }]));
            }
            
            // Reset form data
            setFormData({
                name: '',
                description: '',
                salon: selectedSalonId || ''
            });
            
            // Call success callback with created team payload and close dialog
            onSuccess && onSuccess(t('pro_team_member.add_team.team_created_success'), result);
            onClose();
        } catch (error) {
            console.error('Failed to create team:', error);
            
            // Handle API validation errors
            if (error.data && error.data.errors) {
                setErrors(error.data.errors);
            } else if (error.data) {
                // Handle non-field errors or other error formats
                const apiErrors = {};
                
                if (error.data.name) apiErrors.name = error.data.name.join(' ');
                if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
                if (error.data.description) apiErrors.description = error.data.description.join(' ');
                if (error.data.non_field_errors) {
                    // Show non-field errors in the name field for visibility
                    apiErrors.name = error.data.non_field_errors.join(' ');
                }
                
                setErrors(apiErrors);
            }
        }
    };
    
    // Reset form and close dialog when onClose is called
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
            // Reset form data when dialog is closed
            setFormData({
                name: '',
                description: '',
                salon: selectedSalonId || ''
            });
            setErrors({});
        } else {
            setDialogClosed(false);
        }
    }, [open, selectedSalonId]);
    
    // Set selected salon ID when it's provided as prop
    useEffect(() => {
        if (selectedSalonId) {
            setFormData(prev => ({
                ...prev,
                salon: selectedSalonId
            }));
        }
    }, [selectedSalonId]);

    // Form content to be displayed in the dialog
    const formContent = (
        <>
            <TextField
                fullWidth
                label={t('pro_team_member.add_team.name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                disabled={isCreating}
            />
            
            <TextField
                fullWidth
                label={t('pro_team_member.add_team.description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isCreating}
            />
            
            {/* Salon Selection - only show if no selectedSalonId is provided */}
            {!selectedSalonId && (
                <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.salon}
                    disabled={isCreating || salonsLoading || profileLoading}
                >
                    <InputLabel>{t('pro_team_member.add_team.salon')}</InputLabel>
                    <Select
                        name="salon"
                        value={formData.salon}
                        onChange={handleChange}
                        label={t('pro_team_member.add_team.salon')}
                    >
                        {salonsLoading ? (
                            <MenuItem disabled>{t('pro_team_member.add_team.loading_salons')}</MenuItem>
                        ) : salonsData && salonsData.length > 0 ? (
                            salonsData.map(salon => (
                                <MenuItem key={salon.salon_id} value={salon.salon_id}>
                                    {salon.name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>{t('pro_team_member.add_team.no_salons_available')}</MenuItem>
                        )}
                    </Select>
                    {errors.salon && <FormHelperText>{errors.salon}</FormHelperText>}
                </FormControl>
            )}
        </>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <MuiDialog
            open={open}
            icon={<GroupsIcon />}
            triggerButton={triggerButton}
            title={t('pro_team_member.add_team.title')}
            subtitle={t('pro_team_member.add_team.subtitle')}
            content={formContent}
            existAction={true}
            actionTitle={t('pro_team_member.add_team.create')}
            actionFunction={handleSubmit}
            onClose={dialogClosed}
            onExternalClose={onClose}
            isLoading={isCreating}
            actionType="button"
            maxWidth="sm"
            disableAction={isCreating}
        />
    );
};

export default AddTeam;